import type DOMPurify from 'dompurify';

export type AllowedAttributes = {
  [tag: string]: string[];
};

/**
 * Default allowed HTML tags for content conversion
 */
export const defaultAllowedTags: string[] = [
  'p',
  'span',
  'strong',
  'em',
  'ul',
  'ol',
  'li',
  'a',
  'table',
  'tr',
  'td',
  'th',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'code',
  'img',
  'br',
  'hr',
  'mark',
  'div',
  'col',
  'colgroup',
  'b',
  'u',
  'i',
  's',
  'strike',
  'del',
  '#text',
  'insert',
  'delete',
];

/**
 * Default allowed attributes per tag for content conversion
 */
export const defaultAllowedAttributes: AllowedAttributes = {
  a: ['href', 'title', 'target', 'rel', 'id', 'class'],
  img: ['src', 'alt', 'title', 'width', 'height', 'data-attachment-id', 'data-manual-id'],
  table: ['width', 'align', 'border', 'cellspacing', 'cellpadding', 'style'],
  tr: ['align', 'style'],
  td: ['width', 'align', 'colspan', 'rowspan', 'style', 'bgcolor'],
  th: ['width', 'align', 'colspan', 'rowspan', 'style', 'bgcolor'],
  col: ['width'],
  colgroup: ['width'],
  span: ['style', 'data-created-at'],
  ol: ['data-list-type', 'type'],
  ul: ['data-list-type', 'type'],
  '*': ['class', 'id', 'data-*', 'contenteditable', 'contentEditable', 'style'],
};

/**
 * URI pattern that allows standard protocols plus xref:// for cross-references
 */
export const ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xref):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

/**
 * Common attributes needed across all conversion methods
 */
const commonAllowedAttrs = [
  'title',
  'class',
  'data-textentity-data',
  'data-attachment-id',
  'data-manual-id',
  'data-image-placeholder',
  'href',
  'id',
  'width',
  'height',
  'alt',
  'target',
  'rel',
  'data-username',
  'data-created-at',
];

/**
 * Creates DOMPurify config for editor content (legacy → editor conversion)
 */
export const createEditorSanitizeConfig = (
  allowedTags: string[],
  allowedAttributes: AllowedAttributes
): DOMPurify.Config => ({
  ALLOWED_TAGS: [...allowedTags, 'lexientity', 'lexindex', 'span', 'insert', 'delete'],
  ALLOWED_ATTR: [...Object.values(allowedAttributes).flat(), ...commonAllowedAttrs],
  ADD_TAGS: ['lexientity', 'lexindex'],
  ALLOWED_URI_REGEXP,
  KEEP_CONTENT: true,
  WHOLE_DOCUMENT: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM: false,
});

/**
 * Creates DOMPurify config for render content (editor → render conversion)
 */
export const createRenderSanitizeConfig = (
  allowedTags: string[],
  allowedAttributes: AllowedAttributes
): DOMPurify.Config => ({
  ALLOWED_TAGS: [...allowedTags, 'lexientity', 'lexindex', 'span', 'insert', 'delete'],
  ALLOWED_ATTR: [...Object.values(allowedAttributes).flat(), ...commonAllowedAttrs, 'style', 'data-list-type'],
  ALLOWED_URI_REGEXP,
  KEEP_CONTENT: true,
});
